import { Amount } from '@/components/common/Amount';
import { Menu } from '@/components/common/Menu';
import { SvgDotsVertical } from '@/components/icons';
import type { BudgetMonth } from '@/api/types';
import { send } from '@/api/send';
import { useBudgetStore } from '@/store/budget';
import { theme } from '@/style/theme';
import { longFromMonth, shortFromMonth } from '@/utils/months';

type SummaryCardProps = {
  data: BudgetMonth;
  isCurrent: boolean;
};

type Row = { label: string; value: number };

/**
 * The per-month summary card: Available Funds / Overspent / Budgeted / For Next
 * Month, then the To Budget figure. Port of
 * `budget/envelope/budgetsummary/{BudgetSummary,ToBudget,TotalsList}.tsx`.
 */
export function BudgetSummaryCard({ data, isCurrent }: SummaryCardProps) {
  const { summary } = data;
  const reload = useBudgetStore(state => state.load);
  const overbudgeted = summary.toBudget < 0;

  const rows: Row[] = [
    { label: '可用资金', value: summary.availableFunds },
    {
      label: summary.overspentMonth
        ? `${shortFromMonth(summary.overspentMonth)}超支`
        : '历史超支',
      value: summary.overspent,
    },
    { label: '已预算', value: summary.budgeted },
    { label: '留给下月', value: summary.forNextMonth },
  ];

  async function coverOverbudgeted() {
    await send('envelope-budget-cover', { month: data.month });
    await reload();
  }

  return (
    <section
      className="w-[400px] max-w-full shrink-0 overflow-hidden rounded-md border border-table-border bg-card-background shadow-card"
      data-testid={`budget-summary-${data.month}`}
    >
      <header className="relative flex items-center justify-center py-4">
        <h2
          className={
            isCurrent
              ? 'text-xl font-bold text-page-text'
              : 'text-xl font-bold text-page-text-light'
          }
        >
          {longFromMonth(data.month)}
        </h2>
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <Menu
            width={230}
            data-testid="budget-summary-menu"
            button={
              <span className="block cursor-pointer rounded-sm p-1 text-page-text-light hover:bg-button-bare-background-hover">
                <SvgDotsVertical width={15} height={15} />
              </span>
            }
            items={[
              {
                label: '弥补超支',
                disabled: !overbudgeted,
                onSelect: () => void coverOverbudgeted(),
              },
            ]}
          />
        </div>
      </header>

      <div className="border-t border-table-border px-5 py-3">
        <div className="mx-auto grid w-full grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1.5">
          {rows.map(row => (
            <div key={row.label} className="contents">
              <span className="text-sm text-page-text-light">{row.label}</span>
              <Amount
                value={row.value}
                greyZero
                className="text-sm font-medium text-page-text"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-table-border py-4 text-center">
        <div
          className="text-sm"
          style={{ color: overbudgeted ? theme.numberNegative : theme.pageTextPositive }}
        >
          {overbudgeted ? '已超支：' : '待分配：'}
        </div>
        <div
          className="tnum text-[30px] font-semibold"
          style={{ color: overbudgeted ? theme.numberNegative : theme.pageTextPositive }}
          data-testid="to-budget-amount"
        >
          <Amount value={summary.toBudget} />
        </div>
      </div>
    </section>
  );
}
